import { useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { Pill, SeverityBadge, SubHeader, fmtKES, label } from '@/features/dossier/components/primitives'
import { draftPlay, legalReview, setPlayStatus, type Play, type Surface } from './api'

/** What each play type IS, in one line, at the point of choosing it. */
const PLAY_TYPES: [string, string][] = [
  ['CONTRAST_MESSAGE', 'Our position beside theirs, on one issue'],
  ['RECORD_CHALLENGE', 'Ask them publicly to answer something documented'],
  ['POLICY_REBUTTAL', 'Answer a policy claim on the substance'],
  ['COALITION_APPROACH', 'Talk to a party or bloc about the ground'],
  ['SURROGATE_BRIEFING', 'Brief our own speakers so they say the same thing'],
  ['EARNED_MEDIA', 'Give a journalist a documented story'],
  ['PERSUASION_TARGET', 'Win over their soft supporters — measured as swing TOWARD us'],
  ['DEBATE_PREP', 'Prepare our candidate for what they will say'],
  ['LEGAL_REFERRAL', 'Refer a matter to a lawyer or a regulator'],
  ['FACT_CHECK_REFERRAL', 'Hand a claim to a fact-checker'],
]

const RISKS = ['LOW', 'MEDIUM', 'HIGH', 'EXTREME']
const REVIEW = ['CLEARED', 'CLEARED_WITH_CONDITIONS', 'REFUSED']

/**
 * The play-book: what we will actually DO about a target.
 *
 * ## A play is the action; a surface is the finding
 *
 * A surface says "this is documented and this is how well". A play says "and here is what we
 * will do about it, to whom, on what channel, at what cost, expecting what movement". The
 * DEPLOY gate refuses a target with no play for exactly that reason: an operation with
 * findings and no actions is a research project, and deploying it means deploying nothing.
 *
 * ## Every play starts DRAFT and needs somebody ELSE to clear it
 *
 * That is the control this whole feature rests on. A legal review by the author is not a
 * review, it is a signature — so the service refuses it, and the button below says so before
 * the click rather than after. (A named waiver exists for single-operator deployments; it
 * stamps the audit trail so the two remain tellable apart.)
 *
 * ## Expected swing is measured TOWARD us
 *
 * There is no field here, and no play type, for depressing anybody's turnout. The lawful
 * question is which of their soft supporters can be won — worth twice as much anyway, since a
 * swing moves two votes and a suppression moves one.
 */
export function PlaybookPanel({ plays, surfaces, targetId, canPlan, canReview, onChanged }: {
  plays: Play[]
  surfaces: Surface[]
  targetId: string
  /** `opposition:plan` — drafting. */
  canPlan: boolean
  /** `opposition:legal-review` — clearing. Deliberately a different authority. */
  canReview: boolean
  onChanged: () => void
}) {
  const [drafting, setDrafting] = useState(false)
  const [reviewing, setReviewing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [playTypeCode, setPlayTypeCode] = useState('CONTRAST_MESSAGE')
  const [title, setTitle] = useState('')
  const [tactic, setTactic] = useState('')
  const [executionPlan, setExecutionPlan] = useState('')
  const [surfaceId, setSurfaceId] = useState('')
  const [audienceSegment, setAudienceSegment] = useState('')
  const [channels, setChannels] = useState('')
  const [expectedSwingPp, setExpectedSwingPp] = useState('')
  const [riskLevelCode, setRiskLevelCode] = useState('MEDIUM')
  const [budgetKes, setBudgetKes] = useState('')
  const [timelineLabel, setTimelineLabel] = useState('')

  const [reviewStatus, setReviewStatus] = useState('CLEARED')
  const [conditions, setConditions] = useState('')
  const [reviewNote, setReviewNote] = useState('')

  async function draft(event: React.FormEvent) {
    event.preventDefault()
    if (!title.trim() || !tactic.trim()) return
    setBusy(true)
    setError(null)
    try {
      await draftPlay(targetId, {
        surfaceId: surfaceId || null,
        playTypeCode,
        title: title.trim(),
        tactic: tactic.trim(),
        executionPlan: executionPlan.trim() || null,
        audienceSegment: audienceSegment.trim() || null,
        channels: channels.split(',').map((c) => c.trim()).filter(Boolean),
        expectedSwingPp: expectedSwingPp.trim() ? Number(expectedSwingPp) : null,
        riskLevelCode,
        budgetKes: budgetKes.trim() ? Number(budgetKes) : null,
        timelineLabel: timelineLabel.trim() || null,
      })
      setDrafting(false)
      setTitle(''); setTactic(''); setExecutionPlan(''); setAudienceSegment('')
      setChannels(''); setExpectedSwingPp(''); setBudgetKes(''); setTimelineLabel('')
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function review(playId: string) {
    setBusy(true)
    setError(null)
    try {
      await legalReview(playId, {
        statusCode: reviewStatus,
        conditions: conditions.trim() || null,
        note: reviewNote.trim() || null,
      })
      setReviewing(null)
      setConditions(''); setReviewNote('')
      onChanged()
    } catch (err) {
      // The refusal names the reason — "a review by the author is a signature" — verbatim.
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function status(playId: string, statusCode: string) {
    setBusy(true)
    setError(null)
    try {
      await setPlayStatus(playId, statusCode)
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SubHeader>Play-book</SubHeader>
        {canPlan && (
          <button type="button" onClick={() => setDrafting((v) => !v)}
                  className="rounded border border-border px-2 py-1 text-[11px]">
            {drafting ? 'Close' : 'Draft a play'}
          </button>
        )}
      </div>

      {error && <ErrorStrip message={error} />}

      <p className="text-[11px] text-muted-foreground">
        A surface is a FINDING; a play is what we will DO about it — to whom, on what channel,
        at what cost, expecting what movement. Deploy refuses a target with no play, because an
        operation with findings and no actions deploys nothing.
      </p>

      {drafting && (
        <form onSubmit={draft} className="space-y-2 rounded border border-border p-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Type
              </span>
              <select value={playTypeCode} onChange={(e) => setPlayTypeCode(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                {PLAY_TYPES.map(([code]) => (
                  <option key={code} value={code}>{label(code)}</option>
                ))}
              </select>
              <span className="text-[10px] text-muted-foreground">
                {PLAY_TYPES.find(([code]) => code === playTypeCode)?.[1]}
              </span>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Answers which exposure (optional)
              </span>
              <select value={surfaceId} onChange={(e) => setSurfaceId(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                <option value="">Not tied to one</option>
                {surfaces.map((s) => (
                  <option key={s.id} value={s.id}>{s.surface}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Title
            </span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required
                   placeholder="Ask him to publish the tender file"
                   className="h-7 rounded border border-input bg-background px-2 text-[12px]" />
          </label>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Tactic — what we actually say or do
            </span>
            <textarea value={tactic} onChange={(e) => setTactic(e.target.value)} rows={2}
                      required
                      className="rounded border border-input bg-background px-2 py-1
                                 text-[12px]" />
          </label>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Execution plan (optional)
            </span>
            <textarea value={executionPlan} rows={2}
                      onChange={(e) => setExecutionPlan(e.target.value)}
                      placeholder="Who does what, in what order."
                      className="rounded border border-input bg-background px-2 py-1
                                 text-[12px]" />
          </label>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Audience
              </span>
              <input value={audienceSegment} onChange={(e) => setAudienceSegment(e.target.value)}
                     placeholder="Undecided under-35s in Westlands"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Channels (comma separated)
              </span>
              <input value={channels} onChange={(e) => setChannels(e.target.value)}
                     placeholder="radio, X, door-to-door"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Expected swing (pp)
              </span>
              <input type="number" step="0.1" value={expectedSwingPp}
                     onChange={(e) => setExpectedSwingPp(e.target.value)}
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
              <span className="text-[10px] text-muted-foreground">
                {/* Said at the field, because it is the one number people misread. */}
                Toward US. There is no field for the opposite, and no play type for it.
              </span>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Risk
              </span>
              <select value={riskLevelCode} onChange={(e) => setRiskLevelCode(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                {RISKS.map((r) => <option key={r} value={r}>{label(r)}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Budget (KES)
              </span>
              <input type="number" value={budgetKes}
                     onChange={(e) => setBudgetKes(e.target.value)}
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Timeline
              </span>
              <input value={timelineLabel} onChange={(e) => setTimelineLabel(e.target.value)}
                     placeholder="Week of the debate"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
          </div>

          <p className="text-[11px] text-muted-foreground">
            It will be saved as <b>DRAFT</b>. It cannot go live until somebody other than you
            records a legal review.
          </p>
          <button type="submit" disabled={busy || !title.trim() || !tactic.trim()}
                  className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                             text-primary-foreground disabled:opacity-50">
            {busy ? 'Saving…' : 'Save the draft'}
          </button>
        </form>
      )}

      {plays.length === 0 ? (
        <p className="py-2 text-[12px] text-muted-foreground">
          No plays drafted. A play starts DRAFT and needs a legal review by somebody other than
          its author to leave it.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {plays.map((p) => {
            const cleared = p.legalReviewStatusCode === 'CLEARED'
              || p.legalReviewStatusCode === 'CLEARED_WITH_CONDITIONS'
            return (
              <li key={p.id} className="rounded border border-border p-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-48 flex-1">
                    <p className="text-[12px] font-medium">{p.title}</p>
                    <p className="text-[11px] text-muted-foreground">{p.tactic}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Pill className="border-border">{label(p.playTypeCode)}</Pill>
                    <SeverityBadge severity={p.riskLevelCode} />
                    <Pill className="border-border">{label(p.statusCode)}</Pill>
                  </div>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px]
                                text-muted-foreground">
                  {p.audienceSegment && <span>{p.audienceSegment}</span>}
                  {p.timelineLabel && <span>{p.timelineLabel}</span>}
                  {p.budgetKes != null && <span>{fmtKES(p.budgetKes)}</span>}
                  {p.expectedSwingPp != null && (
                    <span>+{p.expectedSwingPp}pp toward us</span>
                  )}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <Pill className={p.legalReviewStatusCode === 'REFUSED'
                    ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                    : p.legalReviewStatusCode === 'NOT_REVIEWED'
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'}>
                    {label(p.legalReviewStatusCode)}
                  </Pill>
                  {p.legalConditions && (
                    <span className="text-[10px] text-muted-foreground">
                      conditions: {p.legalConditions}
                    </span>
                  )}

                  {canReview && p.legalReviewStatusCode !== 'REFUSED' && (
                    <button type="button"
                            onClick={() => setReviewing(reviewing === p.id ? null : p.id)}
                            className="rounded border border-border px-2 py-0.5 text-[11px]">
                      {reviewing === p.id ? 'Close' : 'Record a legal review'}
                    </button>
                  )}

                  {/* Only offered once it is cleared — the server refuses otherwise, and a
                      button that exists to be rejected is a button that teaches nothing. */}
                  {canPlan && cleared && p.statusCode === 'READY' && (
                    <button type="button" disabled={busy}
                            onClick={() => void status(p.id, 'LIVE')}
                            className="rounded bg-primary px-2 py-0.5 text-[11px]
                                       text-primary-foreground disabled:opacity-50">
                      Go live
                    </button>
                  )}
                  {canPlan && p.statusCode === 'LIVE' && (
                    <button type="button" disabled={busy}
                            onClick={() => void status(p.id, 'COMPLETE')}
                            className="rounded border border-border px-2 py-0.5 text-[11px]">
                      Mark complete
                    </button>
                  )}
                </div>

                {reviewing === p.id && (
                  <div className="mt-2 space-y-1.5 rounded border border-dashed border-border
                                  p-2">
                    <p className="text-[11px] text-muted-foreground">
                      A review by the play's author is refused — it is a signature, not a
                      review. Your name and the decision go on the record either way.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col gap-0.5">
                        <span className="text-[10px] uppercase tracking-wide
                                         text-muted-foreground">Decision</span>
                        <select value={reviewStatus}
                                onChange={(e) => setReviewStatus(e.target.value)}
                                className="h-7 rounded border border-input bg-background px-2
                                           text-[12px]">
                          {REVIEW.map((r) => <option key={r} value={r}>{label(r)}</option>)}
                        </select>
                      </label>
                      <label className="flex flex-1 flex-col gap-0.5">
                        <span className="text-[10px] uppercase tracking-wide
                                         text-muted-foreground">
                          Conditions (if cleared with conditions)
                        </span>
                        <input value={conditions} onChange={(e) => setConditions(e.target.value)}
                               className="h-7 rounded border border-input bg-background px-2
                                          text-[12px]" />
                      </label>
                    </div>
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] uppercase tracking-wide
                                       text-muted-foreground">Note</span>
                      <textarea value={reviewNote} rows={2}
                                onChange={(e) => setReviewNote(e.target.value)}
                                className="rounded border border-input bg-background px-2 py-1
                                           text-[12px]" />
                    </label>
                    <button type="button" disabled={busy} onClick={() => void review(p.id)}
                            className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                       text-primary-foreground disabled:opacity-50">
                      {busy ? 'Recording…' : 'Record the review'}
                    </button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
